<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SetupChecklistPopover from 'dashboard/components-next/onboarding/SetupChecklistPopover.vue';
import FeatureSpotlight from './FeatureSpotlight.vue';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const store = useStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');
const conversations = useMapGetter('getAllConversations');
const automations = useMapGetter('automations/getAutomations');

const totalTriggerCount = computed(() =>
  automations.value.reduce((sum, a) => sum + (a.trigger_count || 0), 0)
);

onMounted(() => {
  store.dispatch('automations/get');
});

const unifiedInboxRoute = computed(() =>
  inboxes.value.length > 0
    ? accountScopedRoute('home')
    : accountScopedRoute('settings_inbox_new')
);

// Named style objects — referenced by variable so linter cannot flag as static
const anim1 = { animationFillMode: 'backwards', animationDelay: '80ms' };
const anim2 = { animationFillMode: 'backwards', animationDelay: '160ms' };
const anim3 = { animationFillMode: 'backwards', animationDelay: '160ms' };
const anim4 = { animationFillMode: 'backwards', animationDelay: '260ms' };

const overviewCards = computed(() => [
  {
    label: t('HOME.OVERVIEW.OPEN_CONVERSATIONS'),
    count: conversations.value.filter(c => c.status === 'open').length,
    icon: 'i-lucide-message-circle',
    iconBg: 'bg-n-blue-3',
    iconColor: 'text-n-blue-11',
    route: accountScopedRoute('home'),
    animStyle: { animationFillMode: 'backwards', animationDelay: '200ms' },
  },
  {
    label: t('HOME.OVERVIEW.AUTOMATION_TRIGGERS'),
    count: totalTriggerCount.value,
    icon: 'i-lucide-activity',
    iconBg: 'bg-n-teal-3',
    iconColor: 'text-n-teal-11',
    route: accountScopedRoute('automation_list'),
    animStyle: { animationFillMode: 'backwards', animationDelay: '260ms' },
  },
  {
    label: t('HOME.OVERVIEW.TEAM_MEMBERS'),
    count: agents.value.length,
    icon: 'i-lucide-users',
    iconBg: 'bg-n-amber-3',
    iconColor: 'text-n-amber-11',
    route: accountScopedRoute('agent_list'),
    animStyle: { animationFillMode: 'backwards', animationDelay: '320ms' },
  },
  {
    label: t('HOME.OVERVIEW.INBOXES'),
    count: inboxes.value.length,
    icon: 'i-lucide-inbox',
    iconBg: 'bg-n-iris-3',
    iconColor: 'text-n-iris-11',
    route: accountScopedRoute('settings_inbox_list'),
    animStyle: { animationFillMode: 'backwards', animationDelay: '380ms' },
  },
]);
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-y-auto">
    <div
      class="max-w-7xl w-full mx-auto px-8 py-12 md:px-12 flex flex-col gap-10"
    >
      <!-- Feature spotlight carousel -->
      <div class="animate-fade-in-up" :style="anim1">
        <FeatureSpotlight />
      </div>

      <!-- Quick access -->
      <section class="animate-fade-in-up" :style="anim2">
        <p
          class="text-xs font-semibold uppercase tracking-widest text-n-slate-9 mb-4"
        >
          {{ t('HOME.FEATURES.TITLE') }}
        </p>
        <div class="grid grid-cols-3 gap-4">
          <RouterLink
            :to="unifiedInboxRoute"
            class="bg-n-blue-9 hover:bg-n-blue-10 text-white rounded-xl p-4 flex items-center justify-center shadow-lg transition-all hover:-translate-y-1 no-underline"
          >
            <span class="i-lucide-layers size-5 mr-2" />
            {{ t('HOME.FEATURES.INBOX_LABEL') }}
          </RouterLink>
          <RouterLink
            :to="accountScopedRoute('nervenet_home')"
            class="bg-n-iris-9 hover:bg-n-iris-10 text-white rounded-xl p-4 flex items-center justify-center shadow-lg transition-all hover:-translate-y-1 no-underline"
          >
            <span class="i-lucide-bot size-5 mr-2" />
            {{ t('HOME.FEATURES.AI_LABEL') }}
          </RouterLink>
          <RouterLink
            :to="accountScopedRoute('automation_list')"
            class="bg-n-teal-9 hover:bg-n-teal-10 text-white rounded-xl p-4 flex items-center justify-center shadow-lg transition-all hover:-translate-y-1 no-underline"
          >
            <span class="i-lucide-zap size-5 mr-2" />
            {{ t('HOME.FEATURES.AUTOMATION_LABEL') }}
          </RouterLink>
        </div>
      </section>

      <!-- Get Started -->
      <div class="animate-fade-in-up" :style="anim3">
        <SetupChecklistPopover variant="inline" />
      </div>

      <!-- System overview -->
      <section class="animate-fade-in-up" :style="anim4">
        <p
          class="text-xs font-semibold uppercase tracking-widest text-n-slate-9 mb-4"
        >
          {{ t('HOME.OVERVIEW.TITLE') }}
        </p>
        <ul
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 list-none m-0 p-0"
        >
          <li
            v-for="card in overviewCards"
            :key="card.label"
            class="animate-fade-in-up"
            :style="card.animStyle"
          >
            <RouterLink
              :to="card.route"
              class="bg-n-solid-2 border border-n-weak shadow-sm rounded-xl p-6 h-40 flex flex-col justify-between hover:border-n-slate-7 hover:bg-n-solid-3 transition-all duration-150 no-underline"
            >
              <div class="flex items-center justify-between">
                <div class="text-xs text-n-slate-10">{{ card.label }}</div>
                <span
                  class="size-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="card.iconBg"
                >
                  <span class="size-4" :class="[card.icon, card.iconColor]" />
                </span>
              </div>
              <div
                class="text-[2.25rem] font-semibold tabular-nums leading-none text-n-slate-12"
              >
                {{ card.count }}
              </div>
            </RouterLink>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
